import { useCallback, useEffect, useRef } from 'react';
import { EntityStore } from '@app/store/createEntityStore';
import { useApiHealthStore } from '@app/store/apiHealthStore';
import { ApiError } from '@app/utils/ApiError';

export interface UseEntityResult<T> {
  items: T[];
  isLoaded: boolean;
  /** Why the load failed, or null. Render DataLoadError with this instead of an empty list. */
  error: ApiError | null;
  /** Re-runs the load, bypassing the cache. Awaitable, so callers can show progress. */
  retry: () => Promise<void>;
}

export const useEntity = <T extends { id: string | number }>(
  useStore: (selector: (state: EntityStore<T>) => any) => any
): UseEntityResult<T> => {
  const items = useStore((state) => state.items);
  const isLoaded = useStore((state) => state.isLoaded);
  const error = useStore((state) => state.error);
  const loadAll = useStore((state) => state.loadAll);
  const subscribeView = useStore((state) => state.subscribeView);

  // Bumped when the backend comes back, or when the user hits retry in the banner
  const retryToken = useApiHealthStore((state) => state.retryToken);

  const lastFocusTime = useRef<number>(Date.now());

  // Read inside effects that must not re-run when it changes
  const isLoadedRef = useRef(isLoaded);
  isLoadedRef.current = isLoaded;

  const retry = useCallback(() => loadAll(true), [loadAll]);

  useEffect(() => {
    // 1. Tell store a component is actively displaying this entity
    const unsubscribe = subscribeView();

    // 2. Initial load. loadAll never rejects — a failure lands on store.error,
    // which this hook returns for the view to render.
    if (!isLoaded) {
      void loadAll();
    }

    // 3. Tab Visibility Handler: Refetch if tab was hidden for a long time
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        const timeAwayMs = Date.now() - lastFocusTime.current;
        // If away for more than 5 minutes, force a silent background refresh
        if (timeAwayMs > 5 * 60 * 1000) {
          void loadAll(true); // forceRefresh = true
        }
      } else {
        lastFocusTime.current = Date.now();
      }
    };

    window.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      unsubscribe(); // Decrements active view count in store
      window.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isLoaded, loadAll, subscribeView]);

  // Pick up automatically once the service is reachable again, so the user doesn't
  // have to retry every open page by hand. Only stores that hold no data retry;
  // in-flight duplicates are collapsed by the store itself.
  useEffect(() => {
    if (retryToken === 0) return;
    if (!isLoadedRef.current) void loadAll(true);
  }, [retryToken, loadAll]);

  return { items, isLoaded, error, retry };
};
